/**
 * Verification domain types — camelCase mapped from DB snake_case.
 *
 * Based on identity_documents + driver_licenses tables and the canonical
 * provider abstraction in supabase/functions/_shared/verification/types.ts.
 */

export type VerificationStatus = 'pending' | 'submitted' | 'approved' | 'rejected' | 'expired'

/**
 * Canonical status used by the provider abstraction. Kept separate from
 * the legacy `VerificationStatus` so existing hooks keep compiling while
 * the new photo-first flow uses the canonical values directly.
 */
export type CanonicalVerificationStatus =
  | 'pending'
  | 'in_progress'
  | 'approved'
  | 'declined'
  | 'review'
  | 'expired'

export type VerificationProviderName =
  | 'verifik'
  | 'didit'
  | 'claude-vision'
  | 'document-ai'
  | 'sertracen'
  | 'manual'

export type IdDocumentType = 'dui' | 'passport' | 'license' | 'cedula' | 'nie'

export type LicenseClass = 'liviana' | 'pesada' | 'motocicleta' | 'especial'

export type VerificationStepType = 'identity' | 'license' | 'selfie' | 'disclaimer'

export interface VerificationStep {
  type: VerificationStepType
  status: VerificationStatus
  rejectionReason: string | null
}

/**
 * Structured data extracted from an identity document, either by Didit's
 * sync /v3/id-verification/ endpoint or by Claude Vision OCR. Every field
 * is optional because the caller may have only partial coverage.
 */
export interface ExtractedIdentityData {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  dateOfBirth?: string | null
  documentNumber?: string | null
  duiNumber?: string | null
  controlPrefix?: string | null
  controlNumber?: string | null
  licenseClass?: LicenseClass | string | null
  documentType?: string | null
  issuingCountry?: string | null
  issueDate?: string | null
  expiryDate?: string | null
  nationality?: string | null
  sex?: 'M' | 'F' | null
  mrz?: string | null
  warnings?: string[]
}

export interface IdentityDocumentInput {
  documentType: IdDocumentType
  documentNumber: string
  firstName: string
  lastName: string
  dateOfBirth: string
  issueDate: string | null
  expiryDate: string | null
  frontImageUri: string
  backImageUri: string
}

export interface DriverLicenseInput {
  licenseNumber: string
  licenseClass: LicenseClass
  issueDate: string
  expiryDate: string
  frontImageUri: string
  backImageUri: string
}

export interface SelfieInput {
  selfieUri: string
}

export type LiveSelfieStatus =
  | 'created'
  | 'not_started'
  | 'in_progress'
  | 'approved'
  | 'declined'
  | 'review'
  | 'abandoned'
  | 'expired'
  | 'error'

export interface LiveSelfieSession {
  sessionId: string
  providerSessionId: string
  sessionToken: string | null
  status: LiveSelfieStatus
  url: string | null
  expiresAt: string | null
}

export interface LiveSelfieDecision {
  sessionId: string
  providerSessionId: string
  status: LiveSelfieStatus
  livenessScore: number | null
  faceMatchScore: number | null
  rejectionReason: string | null
}

export interface VerificationOverview {
  identity: VerificationStep
  license: VerificationStep
  selfie: VerificationStep
  /**
   * Whether the current Qualitas declaration wording is on file.
   *
   * This is NOT part of `isFullyVerified`. Guest KYC is identity + licence +
   * selfie — the same three facts `has_complete_verification` uses. The
   * declaration is accepted on the AML screen (booking/hosting), together
   * with the questionnaire, so we never ask PEP/income of someone who is
   * only verifying a DUI.
   */
  disclaimer: VerificationStep
  /** Guest-ready: DUI + license + selfie (matches `has_complete_verification`). */
  isFullyVerified: boolean
  /**
   * Host-ready to publish listings: DUI + selfie only.
   * License is NOT required to host — matches `has_host_identity_for_listing`.
   */
  isHostPublishReady: boolean
}

/**
 * Input for the photo-first submission flow. The user has captured front
 * (and optionally back) photos via DocumentScanner; the caller doesn't
 * know the document fields yet — those come back from the edge function's
 * OCR / Verifik response.
 */
export interface PhotoSubmitInput {
  documentType: IdDocumentType
  issuingCountry: string
  /**
   * Provincia o estado emisor.
   *
   * Solo para registros sub-nacionales: Estados Unidos y Canadá no tienen
   * registro nacional de licencias, cada jurisdicción lleva el suyo, y la ruta
   * a consultar los lleva dentro. Sin esto no hay consulta que hacer.
   */
  issuingRegion?: string
  frontImageUri: string
  backImageUri?: string
  /**
   * OCR únicamente: lee las fotos y devuelve lo extraído sin consultar el
   * registro. La consulta se dispara después, cuando la persona ya confirmó o
   * corrigió el número y la fecha.
   */
  extractOnly?: boolean
}

/**
 * Response from the photo-first flow: canonical status + extracted fields
 * ready for the ExtractedFieldsCard.
 */
export interface PhotoSubmitResult {
  /** Registry verification is complete, but biometric evidence is still required. */
  nextStep?: 'selfie'
  recordId?: string
  identityDocumentId?: string
  driverLicenseId?: string
  status: CanonicalVerificationStatus
  provider: VerificationProviderName
  extracted: ExtractedIdentityData
  confidence?: number
  rejectionReason?: string | null
  /**
   * True when the result did NOT come from the live provider response but from
   * reconciling the persisted row after the client lost the response (timeout,
   * network drop, app kill). The server keeps processing; the answer was read
   * back instead of re-paying a second call.
   */
  reconciled?: boolean
}

/**
 * A submission that never reached a decision, read back from the persisted
 * row. This is what the identity screen hydrates on mount so a person whose
 * OCR finished AFTER their phone timed out sees the fields without recapturing.
 */
export interface PendingIdentitySubmission {
  recordId: string
  documentType: IdDocumentType
  issuingCountry: string
  issuingRegion: string | null
  canonicalStatus: CanonicalVerificationStatus | null
  /** Raw provider_status column: 'retry_required', 'review', etc. */
  providerStatus: string | null
  /** Legacy mirror column, for rows written before canonical dual-write. */
  legacyStatus: VerificationStatus | null
  extracted: ExtractedIdentityData
  /** At least one stored photo path exists on the row. */
  hasPhotos: boolean
  /** The row carries readable extracted fields. */
  hasExtraction: boolean
  rejectionReason: string | null
}

/**
 * Why a person cannot be charged yet — one entry per unmet condition.
 *
 * The list is authored by `private.booking_eligibility` in the database and mirrored here so
 * the app can say "te falta la licencia" instead of "tu pago fue rechazado". Order matters
 * only for display; the server decides membership.
 */
export const BOOKING_BLOCKERS = [
  /** Asked to be deleted. The one entry that is not a step the person can go and complete. */
  'account_deletion_requested',
  'identity_document',
  'selfie',
  'driver_license',
  'phone',
  'email',
  'aml_declaration',
  /** Every document is approved but the profile flag has not caught up. Nothing to do but wait. */
  'identity_review_pending',
  /** No profile row at all. Fails closed rather than reading as "nothing missing". */
  'unknown_profile',
] as const

export type BookingBlocker = (typeof BOOKING_BLOCKERS)[number]

export interface BookingEligibility {
  allowed: boolean
  missing: BookingBlocker[]
  /**
   * Blocked for a reason this build does not recognise — a newer server naming a condition an
   * older app has no copy for. Shown as "no pudimos determinar qué falta", never as nothing.
   */
  hasUnknownBlocker: boolean
}
